import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInstanceId } from '@frontend/hooks';

import { RAGService } from '../services/RAGService';
import type { RAGConfiguration } from '../types';

export function useRAGConfiguration() {
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();

  // Fetch configurations
  const {
    data,
    isPending: isLoadingConfigurations,
    error: fetchError,
  } = useQuery({
    queryKey: ['rag-configurations', instanceId],
    queryFn: () => {
      if (!instanceId) throw new Error('Instance ID is required');
      return RAGService.getConfigurations(instanceId);
    },
    enabled: !!instanceId,
  });

  // Toggle configuration status
  const {
    mutate: toggleStatus,
    isPending: isTogglingStatus,
    error: toggleError,
  } = useMutation({
    mutationFn: async (turnstileId: string) => {
      if (!instanceId) throw new Error('Instance ID is required');
      return RAGService.toggleConfigurationActivationStatus(instanceId, turnstileId);
    },
    onSuccess: updatedConfig => {
      // Update the specific configuration in the cache
      queryClient.setQueryData<RAGConfiguration[]>(['rag-configurations', instanceId], oldData => {
        if (!oldData) return oldData;
        return oldData.map(config =>
          config.turnstile_id === updatedConfig.turnstile_id ? updatedConfig : config
        );
      });
      toaster.create('Configuration updated successfully', { type: 'success' });
    },
    onError: error => {
      toaster.create(error.message || 'Failed to update configuration', { type: 'danger' });
    },
  });

  return {
    // Configuration data
    configurations: data ?? [],
    isLoadingConfigurations,
    fetchError,

    // Toggle operations
    toggleStatus,
    isTogglingStatus,
    toggleError,
  };
}
